import { Injectable } from '@angular/core';
import { FileApiService } from '@angular-workspace/services';
import { EncryptedUploadService } from './encrypted-upload.service';
import type { EncryptedDatasetManifest } from '../../../lib/encrypted-dataset-upload';

/**
 * TANUH-local wrapper around the shared `FileApiService`.
 *
 * `FileApiService` (libs/services) is used, version-unaware, by every app in this workspace.
 * Rather than adding a version parameter to its methods (which would ripple into every other
 * app's build), this wrapper builds versioned S3 keys (`"<versionTag>/<fileName>"`) at the call
 * site and passes them through the existing, unmodified `key`/`prefix` string parameters.
 *
 * Version-scoped listing reuses `getFilesForPreview`, which already accepts a `prefix` — no
 * changes to the shared `listFiles` method were needed.
 */
@Injectable({ providedIn: 'root' })
export class DatasetVersionFileService {
  constructor(
    private fileApiService: FileApiService,
    private encryptedUploadService: EncryptedUploadService,
  ) {}

  buildVersionedKey(versionTag: string | null, fileName: string): string {
    const v = (versionTag ?? '').trim();
    return v ? `${v}/${fileName}` : fileName;
  }

  /** Version-scoped listing (empty/null versionTag lists everything under the databank). */
  async listVersionFiles(
    databankId: string,
    versionTag: string | null,
  ): Promise<{ data: any; status: number }> {
    const v = (versionTag ?? '').trim();
    return this.fileApiService.getFilesForPreview(databankId, {
      prefix: v ? `${v}/` : undefined,
      recursive: true,
      maxKeys: 1000,
    });
  }

  async initiateUpload(
    databankId: string,
    versionTag: string | null,
    fileName: string,
    contentType: string,
  ): Promise<{ data: any; status: number }> {
    return this.fileApiService.initiateUpload(
      databankId,
      this.buildVersionedKey(versionTag, fileName),
      contentType,
      1,
    );
  }

  async uploadPart(presignedUrl: string, file: File): Promise<any> {
    return this.fileApiService.uploadPart(presignedUrl, file);
  }

  async completeUpload(
    databankId: string,
    uploadId: string,
    versionTag: string | null,
    fileName: string,
    parts: Array<{ partNumber: number; eTag: string }>,
  ): Promise<{ data: any; status: number }> {
    return this.fileApiService.completeUpload(
      databankId,
      uploadId,
      this.buildVersionedKey(versionTag, fileName),
      parts,
    );
  }

  async generatePreview(
    databankId: string,
    versionTag: string | null,
    fileName: string,
  ): Promise<{ data: any; status: number }> {
    return this.fileApiService.generatePreview(
      databankId,
      this.buildVersionedKey(versionTag, fileName),
    );
  }

  /** `fullKey` is the key already returned by `listVersionFiles` — no re-building needed. */
  async deleteFile(databankId: string, fullKey: string): Promise<{ data: any; status: number }> {
    return this.fileApiService.deleteFile(databankId, fullKey);
  }

  async cancelUpload(
    databankId: string,
    uploadId: string,
    fullKey: string,
  ): Promise<{ data: any; status: number }> {
    return this.fileApiService.cancelUpload(databankId, uploadId, fullKey);
  }

  async getPresignedDownloadUrl(
    databankId: string,
    fullKey: string,
  ): Promise<{ presignedUrl: string; expiresAt: string }> {
    return this.fileApiService.getPresignedDownloadUrl(databankId, {
      key: fullKey,
      presigned: true,
    });
  }

  async getFileBlob(databankId: string, fullKey: string): Promise<Blob> {
    return this.fileApiService.getFileBlob(databankId, { key: fullKey, presigned: false });
  }

  /**
   * Client-side-encrypted upload (same AES-256-GCM chunked + KMS-wrapped-DEK flow used by the
   * onboarding upload step), scoped to a version via the same `buildVersionedKey` used by the
   * plain upload path above.
   */
  async uploadEncryptedFile(
    databankId: string,
    versionTag: string | null,
    fileName: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<EncryptedDatasetManifest> {
    return this.encryptedUploadService.uploadEncryptedFile(
      databankId,
      this.buildVersionedKey(versionTag, fileName),
      file,
      onProgress,
    );
  }

  /**
   * Whole-databank scope: `processDatabank` (and the zip/report workers behind it) recursively
   * scan the entire `databankId` prefix, so publishing always reprocesses all versions combined.
   * A per-version publish would require files-connect-api changes — out of scope here.
   */
  async publishDatabank(databankId: string): Promise<{ data: any; status: number }> {
    return this.fileApiService.processDatabank(databankId, 'all');
  }
}
